﻿import { CalendarDays, Clock, Sun, Umbrella, AlertCircle, Wallet, Gift } from 'lucide-react';
import type { PayrollMonthlyRecord } from '@pms/shared';
import { Dialog, DialogContent, DialogOverlay } from '@/components/ui/dialog';

function formatHours(mins: number): string {
  if (!mins || mins <= 0) return '0h 0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatCurrency(val: string | number): string {
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface Props {
  record: PayrollMonthlyRecord;
  open: boolean;
  onClose: () => void;
}

export function SalaryDetailModal({ record, open, onClose }: Props) {
  if (!open) return null;

  const monthName = MONTH_NAMES[(record.month - 1)] ?? '';
  const gross = parseFloat(record.gross_amount);
  const net   = parseFloat(record.net_amount);
  const loanDed = parseFloat(record.loan_deduction);
  const advDed  = parseFloat(record.advance_deduction);
  const penaltyDed = parseFloat(record.penalty_deduction ?? '0');
  const paidHoursAmt = parseFloat((record.paid_hours_amount as string | undefined) ?? '0');
  const paidHoursMins = (record.paid_hours_minutes as number | undefined) ?? 0;
  const rate  = parseFloat(record.per_hour_rate_snapshot);
  const sundayRawH = Math.floor(record.sunday_raw_minutes / 60);
  const sundayRawM = record.sunday_raw_minutes % 60;
  const sundayMultiplier = record.sunday_raw_minutes > 0
    ? (record.sunday_effective_minutes / record.sunday_raw_minutes).toFixed(0)
    : '2';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogOverlay />
      <DialogContent className="max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between rounded-t-lg bg-teal-800 px-5 py-4 text-white -mx-6 -mt-6 mb-5">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            <span className="font-semibold">{monthName} {record.year}</span>
          </div>
        </div>

        {/* Salary summary */}
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div className="rounded-xl border bg-card p-4">
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Gross Salary</p>
            <p className="mt-1 text-2xl font-bold">{formatCurrency(gross)}</p>
          </div>
          <div className="rounded-xl border bg-teal-50 dark:bg-teal-900/20 p-4">
            <p className="text-[11px] font-medium uppercase tracking-wider text-teal-600 dark:text-teal-400">Net Salary</p>
            <p className="mt-1 text-2xl font-bold text-teal-800 dark:text-teal-300">{formatCurrency(net)}</p>
            <p className="text-xs text-muted-foreground mt-1">Take-home</p>
          </div>
        </div>

        {/* Company paid hours credit */}
        {paidHoursAmt > 0 && (
          <div className="mb-4 rounded-lg border border-teal-100 dark:border-teal-900/40 bg-teal-50 dark:bg-teal-900/20 p-3 text-sm flex items-center gap-2">
            <Gift className="h-4 w-4 text-teal-600 dark:text-teal-400 shrink-0" />
            <span className="text-teal-700 dark:text-teal-300">
              Company Paid Hours Credit: <strong>{formatCurrency(paidHoursAmt)}</strong> ({formatHours(paidHoursMins)})
            </span>
          </div>
        )}

        {/* Deductions if any */}
        {(loanDed > 0 || advDed > 0 || penaltyDed > 0) && (
          <div className="mb-4 rounded-lg border border-red-100 bg-red-50 p-3 text-sm">
            <p className="font-medium text-red-700 mb-1">Deductions</p>
            {loanDed > 0    && <p className="text-red-600">Loan EMI: {formatCurrency(loanDed)}</p>}
            {advDed > 0     && <p className="text-red-600">Advance: {formatCurrency(advDed)}</p>}
            {penaltyDed > 0 && <p className="text-red-600">Penalty: {formatCurrency(penaltyDed)}</p>}
          </div>
        )}

        {/* Hours breakdown */}
        <div className="mb-3">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-3">Hours Breakdown</p>
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl border bg-teal-50/60 dark:bg-teal-900/20 p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-teal-100">
                  <Clock className="h-3 w-3 text-teal-700" />
                </div>
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Working</span>
              </div>
              <p className="text-lg font-bold text-teal-700">{formatHours(record.working_minutes)}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Mon-Sat</p>
            </div>

            <div className="rounded-xl border bg-emerald-50/60 dark:bg-emerald-900/20 p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-100">
                  <Clock className="h-3 w-3 text-emerald-700" />
                </div>
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Grace</span>
              </div>
              <p className="text-lg font-bold text-emerald-700">{formatHours(record.grace_minutes)}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Bonus</p>
            </div>

            <div className="rounded-xl border bg-amber-50/60 dark:bg-amber-900/20 p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-amber-100">
                  <Sun className="h-3 w-3 text-amber-700" />
                </div>
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Sunday ({sundayMultiplier}×)</span>
              </div>
              <p className="text-lg font-bold text-amber-700">{formatHours(record.sunday_effective_minutes)}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{sundayRawH}h {sundayRawM}m ×{sundayMultiplier}x</p>
            </div>

            <div className="rounded-xl border bg-violet-50/60 dark:bg-violet-900/20 p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-violet-100">
                  <Umbrella className="h-3 w-3 text-violet-700" />
                </div>
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Leave</span>
              </div>
              <p className="text-lg font-bold text-violet-700">{formatHours(record.paid_leave_minutes)}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Approved</p>
            </div>

            <div className="rounded-xl border bg-rose-50/60 dark:bg-rose-900/20 p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-rose-100">
                  <AlertCircle className="h-3 w-3 text-rose-700" />
                </div>
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Penalty</span>
              </div>
              <p className="text-lg font-bold text-rose-700">{formatHours(record.penalty_minutes)}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Deducted</p>
            </div>

            {paidHoursMins > 0 && (
              <div className="rounded-xl border bg-teal-50/60 dark:bg-teal-900/20 p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-teal-100">
                    <Gift className="h-3 w-3 text-teal-700" />
                  </div>
                  <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Paid</span>
                </div>
                <p className="text-lg font-bold text-teal-700">{formatHours(paidHoursMins)}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{formatCurrency(paidHoursAmt)}</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5 text-sm text-muted-foreground border-t pt-3 mt-1">
          <Wallet className="h-3.5 w-3.5" />
          Per Hour Rate: ₹{rate}/hr
        </div>
      </DialogContent>
    </Dialog>
  );
}
